import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { CalendarFilter } from './api/calendar'

/**
 * Calendar filters, held in the URL.
 *
 * Two reasons this beats component state:
 *
 * - **It survives switching views.** Narrowing to Ms. Kara in the week view and
 *   then opening the month should keep the filter, which it does because every
 *   view reads the same query string.
 * - **It is shareable.** `/calendar/month?instructor=Kara` is a link Tonya can
 *   send a teacher. Component state has no address.
 */
export function useCalendarFilter(): [CalendarFilter, (next: CalendarFilter) => void] {
  const [params, setParams] = useSearchParams()

  const filter = useMemo<CalendarFilter>(
    () => ({
      program: params.get('program') ?? undefined,
      room: params.get('room') ?? undefined,
      instructor: params.get('instructor') ?? undefined,
      style: params.get('style') ?? undefined,
      q: params.get('q') ?? undefined,
      hideCancelled: params.get('hideCancelled') === 'true',
      // D-649. In the URL like every other filter, so a teacher can keep the
      // view she wants and a refresh does not quietly widen it again.
      privatesOnly: params.get('privatesOnly') === 'true',

      // In the URL like every other filter, so the studio view survives a
      // refresh, a back button and a shared link (D-299).
      wholeStudio: params.get('wholeStudio') === 'true',

      /*
       * <b>One dancer's evening</b> (D-606). Set by the conflicts screen, not
       * by a picker: the calendar has filters for programme, room, teacher and
       * style, and a fifth listing three hundred dancers would be a control
       * nobody opens on a screen already carrying four.
       *
       * <b>The name travels with the id</b> so the chip can say whose evening
       * this is. A chip reading <i>Dancer</i> beside an id would be a filter
       * the reader cannot check.
       */
      dancer: params.get('dancer') ?? undefined,
      dancerName: params.get('dancerName') ?? undefined,
    }),
    [params],
  )

  const setFilter = useCallback(
    (next: CalendarFilter) => {
      const updated = new URLSearchParams(params)

      const set = (key: string, value: string | undefined) => {
        if (value?.trim()) updated.set(key, value.trim())
        else updated.delete(key)
      }

      set('program', next.program)
      set('room', next.room)
      set('instructor', next.instructor)
      set('style', next.style)
      set('q', next.q)

      if (next.hideCancelled) updated.set('hideCancelled', 'true')
      else updated.delete('hideCancelled')

      if (next.privatesOnly) updated.set('privatesOnly', 'true')
      else updated.delete('privatesOnly')

      if (next.wholeStudio) updated.set('wholeStudio', 'true')
      else updated.delete('wholeStudio')

      set('dancer', next.dancer)
      set('dancerName', next.dancerName)

      // Replace rather than push: typing in the search box should not fill the
      // back button with one entry per keystroke.
      setParams(updated, { replace: true })
    },
    [params, setParams],
  )

  return [filter, setFilter]
}

/** Carries the current filter across a navigation. */
export function withFilter(path: string, params: URLSearchParams): string {
  const query = params.toString()
  return query ? `${path}?${query}` : path
}
